/**
 * Paint-bucket fill.
 *
 * The canvas is unbounded, so a fill cannot be stored as pixels — it is
 * computed against what is currently on screen and then stored as the
 * *outline* it produced. That keeps it a few hundred numbers instead of a
 * bitmap, lets it scale with zoom like everything else, and means it moves,
 * resizes and undoes through the same machinery as every other op.
 *
 * Three steps:
 *   1. scanline flood fill over the rendered pixels, producing a mask;
 *   2. marching squares around the mask, producing closed rings (holes come
 *      out as their own rings, which even-odd winding then punches out);
 *   3. collinear-point removal, because a pixel-traced ring is mostly straight
 *      runs and would otherwise store one point per pixel.
 *
 * As the user put it: if the shape has a gap, the fill escapes and floods the
 * background. That is inherent to flood filling and is what Paint does too.
 */

/**
 * Flood the region containing (startX, startY).
 *
 * @param {{data: Uint8ClampedArray, width: number, height: number}} image
 * @param {number} startX
 * @param {number} startY
 * @param {number} tolerance 0..1, how different a pixel may be and still count
 *   as part of the same region.
 * @returns {{mask: Uint8Array, width: number, height: number, count: number}}
 */
export function floodMask(image, startX, startY, tolerance = 0.12) {
  const { data, width, height } = image;
  const mask = new Uint8Array(width * height);
  const sx = Math.round(startX);
  const sy = Math.round(startY);
  if (sx < 0 || sy < 0 || sx >= width || sy >= height) {
    return { mask, width, height, count: 0 };
  }

  const at = (i) => i * 4;
  const seed = at(sy * width + sx);
  const sr = data[seed]; const sg = data[seed + 1]; const sb = data[seed + 2]; const sa = data[seed + 3];

  // Compare in straight RGBA distance. Squaring the tolerance once keeps the
  // inner loop to multiplies and adds.
  const limit = (tolerance * 255) ** 2 * 4;
  const matches = (idx) => {
    const p = at(idx);
    const dr = data[p] - sr;
    const dg = data[p + 1] - sg;
    const db = data[p + 2] - sb;
    const da = data[p + 3] - sa;
    return dr * dr + dg * dg + db * db + da * da <= limit;
  };

  // Scanline fill: walk each run to its ends, then push the rows above and
  // below. Far fewer stack entries than pushing every pixel.
  const stack = [sx, sy];
  let count = 0;

  while (stack.length) {
    const y = stack.pop();
    const x = stack.pop();
    const row = y * width;
    if (mask[row + x]) continue;
    if (!matches(row + x)) continue;

    let left = x;
    while (left > 0 && !mask[row + left - 1] && matches(row + left - 1)) left -= 1;
    let right = x;
    while (right < width - 1 && !mask[row + right + 1] && matches(row + right + 1)) right += 1;

    for (let i = left; i <= right; i += 1) {
      mask[row + i] = 1;
      count += 1;
    }

    for (const ny of [y - 1, y + 1]) {
      if (ny < 0 || ny >= height) continue;
      const nrow = ny * width;
      let i = left;
      while (i <= right) {
        // Only seed once per contiguous run on the neighbouring line.
        while (i <= right && (mask[nrow + i] || !matches(nrow + i))) i += 1;
        if (i > right) break;
        stack.push(i, ny);
        while (i <= right && !mask[nrow + i] && matches(nrow + i)) i += 1;
      }
    }
  }

  return { mask, width, height, count };
}

/**
 * Grow the mask by `n` pixels in each direction.
 *
 * Antialiased edges leave a pale halo between a fill and the line that bounds
 * it; without this you can see the paper through the seam. Two pixels of bleed
 * tucks the fill under the line instead.
 */
export function dilate(mask, width, height, n = 2) {
  let current = mask;
  for (let pass = 0; pass < n; pass += 1) {
    const next = new Uint8Array(current);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const i = y * width + x;
        if (current[i]) continue;
        if ((x > 0 && current[i - 1])
          || (x < width - 1 && current[i + 1])
          || (y > 0 && current[i - width])
          || (y < height - 1 && current[i + width])) next[i] = 1;
      }
    }
    current = next;
  }
  return current;
}

/**
 * Trace the boundary of every filled region as a closed ring of points.
 *
 * Marching squares over the corners between pixels, so the rings sit on pixel
 * boundaries and tile exactly against each other. Each ring is a flat
 * [x0, y0, x1, y1, ...] array in mask coordinates.
 */
export function traceRings(mask, width, height, maxRings = 240) {
  const W = width + 1;
  const H = height + 1;
  const solid = (x, y) => (x < 0 || y < 0 || x >= width || y >= height ? 0 : mask[y * width + x]);

  // Which lattice points a boundary has already passed through, so each
  // boundary is walked exactly once.
  const seen = new Uint8Array(W * H);
  const rings = [];

  // The four cells meeting at lattice point (x, y).
  // Bits: 1 = top-left, 2 = top-right, 4 = bottom-right, 8 = bottom-left.
  const state = (x, y) => (solid(x - 1, y - 1) ? 1 : 0)
    | (solid(x, y - 1) ? 2 : 0)
    | (solid(x, y) ? 4 : 0)
    | (solid(x - 1, y) ? 8 : 0);

  // Step so the filled side is always on the left of travel. Each direction
  // is legal only when the cell on its left is filled and the one on its
  // right is not, which for every non-saddle case picks exactly one:
  //   up    needs TL filled, TR empty       down  needs BR filled, BL empty
  //   right needs TR filled, BR empty       left  needs BL filled, TL empty
  // 5 and 10 are the saddles, where two opposite corners are filled and two
  // directions qualify; resolving each the same way every time keeps the walk
  // deterministic, so it always closes.
  const STEP = [
    null,        // 0  nothing here
    [0, -1],     // 1  TL
    [1, 0],      // 2  TR
    [1, 0],      // 3  TL TR
    [0, 1],      // 4  BR
    [0, -1],     // 5  TL BR      (saddle)
    [0, 1],      // 6  TR BR
    [0, 1],      // 7  TL TR BR
    [-1, 0],     // 8  BL
    [0, -1],     // 9  TL BL
    [1, 0],      // 10 TR BL      (saddle)
    [1, 0],      // 11 TL TR BL
    [-1, 0],     // 12 BR BL
    [0, -1],     // 13 TL BR BL
    [-1, 0],     // 14 TR BR BL
    null,        // 15 all filled
  ];

  const limit = W * H + 16;

  for (let y = 0; y < H && rings.length < maxRings; y += 1) {
    for (let x = 0; x < W && rings.length < maxRings; x += 1) {
      if (seen[y * W + x]) continue;
      const s = state(x, y);
      if (!STEP[s]) continue;

      const ring = [];
      let cx = x; let cy = y;
      let guard = 0;

      for (;;) {
        seen[cy * W + cx] = 1;
        ring.push(cx, cy);

        const step = STEP[state(cx, cy)];
        if (!step) break;
        cx += step[0];
        cy += step[1];
        guard += 1;
        if (cx === x && cy === y) break;             // closed
        if (guard > limit) break;                    // cannot happen; cheap insurance
        if (seen[cy * W + cx]) break;                // ran into an earlier boundary
      }

      if (ring.length >= 6) rings.push(ring);
    }
  }

  return rings;
}

/**
 * Drop points that sit on the straight line between their neighbours.
 *
 * A traced ring has a point per pixel step; a rectangle comes out as hundreds
 * of points describing four sides. This collapses each run to its corners.
 */
export function simplifyRing(ring, tolerance = 0.9) {
  // Pass one: drop points that continue in exactly the same direction. On a
  // pixel-traced ring this is most of them, and it is exact — a rectangle
  // comes out as its four corners and nothing else.
  const straightened = [];
  const n0 = ring.length / 2;
  for (let i = 0; i < n0; i += 1) {
    const px = ring[((i - 1 + n0) % n0) * 2]; const py = ring[((i - 1 + n0) % n0) * 2 + 1];
    const cx = ring[i * 2]; const cy = ring[i * 2 + 1];
    const nx = ring[((i + 1) % n0) * 2]; const ny = ring[((i + 1) % n0) * 2 + 1];
    if ((cx - px) * (ny - cy) !== (cy - py) * (nx - cx)) straightened.push(cx, cy);
  }
  if (straightened.length < 6) return ring;

  // Pass two: flatten pixel staircases, where a diagonal edge zigzags by one
  // pixel at a time. A real corner deviates far more than the tolerance and
  // survives.
  const out = [];
  const n = straightened.length / 2;
  for (let i = 0; i < n; i += 1) {
    const px = straightened[((i - 1 + n) % n) * 2]; const py = straightened[((i - 1 + n) % n) * 2 + 1];
    const cx = straightened[i * 2]; const cy = straightened[i * 2 + 1];
    const nx = straightened[((i + 1) % n) * 2]; const ny = straightened[((i + 1) % n) * 2 + 1];

    const ax = nx - px; const ay = ny - py;
    const len = Math.hypot(ax, ay);
    const dev = len < 1e-9
      ? Math.hypot(cx - px, cy - py)
      : Math.abs(ax * (py - cy) - (px - cx) * ay) / len;

    if (dev > tolerance) out.push(cx, cy);
  }

  return out.length >= 6 ? out : straightened;
}
